import {Box, SxProps, Tab, Tabs, Theme} from '@mui/material';
import {useCan, usePersona} from 'src/permissions/hooks';
import {PeopleOutlineRounded} from '@mui/icons-material';
import {ChangeEvent, FC, useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {usersApi} from 'src/api';
import {useBusinessId, useAppSelector, useDefineIsRealEstateRole} from 'src/app/hooks';
import {store} from 'src/app/store';
import {tabSx} from 'src/components/businesses/BusinessTables/BusinessTables.sx';
import AMPModal from 'src/components/common/AMPModal';
import {TabPanel} from 'src/components/common/TabPanel';
import {BusinessForm} from 'src/components/forms/BusinessForm';
import {CompanyForm} from 'src/components/forms/CompanyForm';
import {EmployeeForm} from 'src/components/forms/EmployeeForm';
import {UserForm} from 'src/components/forms/UserForm';
import {UserStatsStrip} from 'src/components/users/UserStatsStrip';
import {UserGap} from 'src/components/users/UserStatsStrip/UserStatsStrip.types';
import {UsersTable} from 'src/components/users/UsersTable';
import {UsersSortFieldEnum} from 'src/enums/users';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {DataType, ListParamsType} from 'src/types';
import {useListQueryState} from 'src/hooks/useListQueryState';
import {UsersSearchRequestData} from 'src/types/users';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {UserTypeEnum} from 'src/enums/common';

type UsersQueryTrigger = ReturnType<typeof usersApi.useLazyGetUsersQuery>[0];

const DEFAULT_PAGE_SIZE = 100;

/**
 * Everything this screen can be looking at, and the only copy of it: each field lives in the query
 * string, so opening one person and coming back restores the view instead of resetting it.
 *
 * Two fields stand in for five of the request's. `userType` is one value where the request carries a
 * list, because the tile and the toolbar select are both single-choice. `gap` is one name where the
 * request carries three flags - `notActivated`, `contractIsSigned: false`, `contractExists: false` -
 * because the three are alternatives and only one can be chosen; a field per flag would make states
 * representable that the interface cannot produce.
 */
const QUERY_DEFAULTS = {
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    keyword: '',
    userType: '',
    gap: '',
    sortField: UsersSortFieldEnum.CREATED as string,
    sortAsc: false,
};

const handleGetUsers =
    (trigger: UsersQueryTrigger, businessId?: number) =>
        async (tableData: UsersSearchRequestData, params: { page: number; size: number }) => {
            store.dispatch(preloaderChangeStatus(true));

            // One default set for everyone. The platform administrator used to get a different one
            // - with SUPER_ADMIN and without COMPANY - from when their list spanned every tenant.
            // Inside one business that only cost them the companies, and the platform's own admins
            // belong to no business, so the request would not return them anyway.
            const finalTypes = tableData.userTypes ? tableData.userTypes : LISTED_USER_TYPES;

            const newData: DataType = {
                ...tableData,
                businessId: tableData.businessId ? tableData.businessId : businessId,
                userTypes: finalTypes,
            };

            // Empty, not falsy. `contractExists: false` and `contractIsSigned: false` are the two
            // filters that mean "the ones without" — dropping every falsy value deleted them on the
            // way out, and the request went off without the filter the user had just clicked.
            Object.keys(newData).forEach((item: string) => {
                const value = newData[item];
                if (value === undefined || value === null || value === '') {
                    delete newData[item];
                }
            });

            const data: UsersSearchRequestData = {
                ...newData,
            };

            try {
                return await trigger({data, params}).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                store.dispatch(preloaderChangeStatus(false));
            }
        };

/**
 * The block and the list are two cards on one page. The top margin is the app bar's: the table used
 * to be first and carried its own, and now that the block is above it the clearance lives here.
 */
const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mt: 2.5,
};

/**
 * The account types this list shows when nothing is chosen.
 *
 * Managers and property owners have a page of their own, and the platform's own administrators
 * belong to no business, so the request would not return them anyway. Named here because the block
 * above the table has to count exactly this set — a tile for a type the list never shows would put a
 * number on screen that the table cannot reach.
 */
const LISTED_USER_TYPES = [
    UserTypeEnum.PRIVATE_DRIVER,
    UserTypeEnum.BUSINESS,
    UserTypeEnum.COMPANY,
    UserTypeEnum.EMPLOYEE,
];

export const Users: FC = () => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const intl = useIntl();
    const persona = usePersona();
    const [open, setOpen] = useState<boolean>(false);
    const dialogTitle = intl.formatMessage({id: 'createNewUser'});
    const {authInfo} = useAppSelector((state) => state.common);
    const [tab, setTab] = useState<number>(0);

    const isRealEstate = useDefineIsRealEstateRole();
    // The permission the endpoint behind the tab checks. Written as "anyone but the platform
    // administrator", it hid the employee form from the account that may create one anywhere.
    const canCreateEmployee = useCan('employees.create');
    // Each tab asks for the permission its own form posts under, not for the one that lets you see
    // the list of the same thing. A technical manager may read every company and create none: the
    // company tab used to open on companies.search and the submit came back 403. The driver tab had
    // the same shape - users.create is what the list's Add button needs, while the form behind the
    // tab posts to /registration/drivers/manual under registration.createDriver.
    const canCreateDriver = useCan('registration.createDriver');
    const canCreateBusiness = useCan('registration.createBusiness');
    const canCreateCompany = useCan('companies.create');
    const navigate = useNavigate();

    const [trigger, response] = usersApi.useLazyGetUsersQuery();
    const [query, setQuery] = useListQueryState(QUERY_DEFAULTS);

    // A boolean rather than `authInfo` itself: the effect below waits for the session to arrive, and
    // depending on the object would re-ask the server every time anything in it was replaced.
    const isSignedIn = Boolean(Object.keys(authInfo).length);

    // Both derived from the address bar rather than kept beside it.
    const filterParams: UsersSearchRequestData = useMemo(() => ({
        keyword: query.keyword,
        businessId: 0,
        userTypes: query.userType ? [query.userType as UserTypeEnum] : undefined,
        notActivated: query.gap === 'notActivated' ? true : undefined,
        contractIsSigned: query.gap === 'awaitingSignature' ? false : undefined,
        contractExists: query.gap === 'withoutContract' ? false : undefined,
        sortAsc: query.sortAsc,
        sortField: query.sortField,
    }), [query]);

    const tableParams: ListParamsType = useMemo(
        () => ({page: query.page, size: query.size}),
        [query.page, query.size]
    );

    const handleOpenModal = () => {
        setOpen(true);
    };
    const handleCloseModal = () => {
        setOpen(false);
        callHandler(filterParams, tableParams);
    };

    // The pills' own filters are left out of the key, and the types are pinned to the listed set so
    // the tiles show the whole breakdown even while the list is narrowed to one of them.
    const statsRequest = useMemo(
        () => ({
            data: {
                ...filterParams,
                businessId: filterParams.businessId || businessId,
                userTypes: LISTED_USER_TYPES,
                contractExists: undefined,
                contractIsSigned: undefined,
                notActivated: undefined,
            },
        }),
        [filterParams, businessId]
    );
    const {data: userStats} = usersApi.useGetUserStatsQuery(statsRequest, {
        skip: !Object.keys(authInfo).length,
    });

    // Type and gap are alternatives, not filters to combine: each selection clears the others, and
    // clicking the active one clears it.
    const handleSelectType = (type: UserTypeEnum) => {
        setQuery({
            ...query,
            page: 0,
            gap: '',
            userType: query.userType === type ? '' : type,
        });
    };

    const handleSelectGap = (gap: UserGap) => {
        setQuery({
            ...query,
            page: 0,
            userType: '',
            gap: query.gap === gap ? '' : gap,
        });
    };

    /**
     * Whatever the list is now asking for, as a whole request body from the table below.
     *
     * Back to the first page, because the row that was on page four of the old filter is not on page
     * four of the new one. The three gap flags come back as the one name they stand for.
     */
    const handleChange = (value: UsersSearchRequestData) => {
        setQuery({
            ...query,
            page: 0,
            keyword: value.keyword ?? '',
            userType: value.userTypes?.length === 1 ? String(value.userTypes[0]) : '',
            gap: value.notActivated
                ? 'notActivated'
                : value.contractIsSigned === false
                    ? 'awaitingSignature'
                    : value.contractExists === false
                        ? 'withoutContract'
                        : '',
            sortField: value.sortField ?? UsersSortFieldEnum.CREATED,
            sortAsc: value.sortAsc ?? false,
        });
    };

    const handleChangePage = (page: number) => setQuery({...query, page});

    const handleChangeRowsNumber = (e: ChangeEvent<HTMLInputElement>) => setQuery({
        ...query,
        page: 0,
        size: +e.target.value || DEFAULT_PAGE_SIZE,
    });

    // Memoised so the effect below can depend on it: `trigger` is stable and the business only
    // changes when a platform administrator switches tenant, which has to re-ask anyway.
    const callHandler = useMemo(() => handleGetUsers(trigger, businessId), [trigger, businessId]);

    const readUsers = useCan('users.search');
    // Gates the private-users tab and the add button: both are about creating and reading people.
    // The button opens a dialog of four forms, so it is offered when at least one of them can be
    // submitted. users.create alone opened an empty dialog for anyone holding it and nothing else.
    const canCreateAnything = canCreateDriver || canCreateBusiness || canCreateCompany
        || canCreateEmployee;

    useEffect(() => {
        // The first tab this caller actually has. Falling through to the business tab by number
        // landed them on a hidden one, and the dialog opened on an empty panel.
        if (canCreateDriver) {
            return;
        }
        const firstAvailable = [
            canCreateBusiness ? 1 : undefined,
            canCreateCompany ? 2 : undefined,
            canCreateEmployee ? 3 : undefined,
        ].find((value) => value !== undefined);
        if (firstAvailable !== undefined) {
            setTab(firstAvailable);
        }
    }, [canCreateDriver, canCreateBusiness, canCreateCompany, canCreateEmployee]);

    // Anyone who may not read the directory is sent to their own page rather than to an empty list.
    useEffect(() => {
        !readUsers && navigate(`/app/users/${authInfo.id}`);
    }, [readUsers, navigate, authInfo.id]);

    /**
     * One place asks the server, whenever the address bar says something else.
     *
     * Every handler above only writes the URL. Before this each of them called the server itself and
     * a mount effect had to put the state back together from `location.state` and a regex over
     * `location.search` - so arriving with a filter already in the URL, which is what Back and a
     * reload both are, fetched the unfiltered list.
     */
    useEffect(() => {
        if (!isSignedIn) {
            return;
        }
        void callHandler(filterParams, tableParams);
    }, [isSignedIn, callHandler, filterParams, tableParams]);

    return (
        <Box sx={pageSx}>
            <UserStatsStrip
                data={userStats}
                activeType={
                    filterParams.userTypes?.length === 1 ? filterParams.userTypes[0] : undefined
                }
                onSelectType={handleSelectType}
                activeGap={
                    filterParams.notActivated
                        ? 'notActivated'
                        : filterParams.contractIsSigned === false
                            ? 'awaitingSignature'
                            : filterParams.contractExists === false
                                ? 'withoutContract'
                                : undefined
                }
                onSelectGap={handleSelectGap}
            />
            {response.data && (
                <UsersTable
                    size={tableParams.size}
                    data={response.data}
                    filters={filterParams}
                    pageNumber={tableParams.page}
                    handleUpdateTableData={handleChange}
                    handleChangePage={handleChangePage}
                    handleChangeRowsNumber={handleChangeRowsNumber}
                    title={intl.formatMessage({id: 'users'})}
                    onAddClick={handleOpenModal}
                    buttonText="addUser"
                    onlyTitle={!canCreateAnything}
                    icon={<PeopleOutlineRounded/>}
                />
            )}
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                {/* Level with the form beneath it. The dialog carries no padding of its own, so the
                    strip started at the very edge while every field below it began 20px in. */}
                <Box sx={{borderBottom: 1, borderColor: 'divider', px: 2.5}}>
                    <Tabs
                        value={tab}
                        onChange={(_event: React.SyntheticEvent, newValue: number) => setTab(newValue)}
                        aria-label="basic tabs example"
                    >
                        {canCreateDriver && (
                            <Tab
                                sx={tabSx}
                                label={intl.formatMessage({id: 'privateUserTabName'})}
                                value={0}
                            />
                        )}
                        {canCreateBusiness && (
                            <Tab
                                sx={tabSx}
                                label={intl.formatMessage({id: 'business'})}
                                value={1}
                            />
                        )}
                        {canCreateCompany && (
                            <Tab
                                sx={tabSx}
                                label={intl.formatMessage({id: 'createNewCompanyTab'})}
                                value={2}
                            />
                        )}
                        {canCreateEmployee && (
                            <Tab
                                sx={tabSx}
                                label={intl.formatMessage({id: 'employee'})}
                                value={3}
                            />
                        )}
                    </Tabs>
                </Box>
                <TabPanel
                    value={tab}
                    index={0}
                >
                    <UserForm
                        handleClose={handleCloseModal}
                        businessId={businessId}
                    />
                </TabPanel>
                <TabPanel
                    value={tab}
                    index={1}
                >
                    <BusinessForm handleClose={handleCloseModal}/>
                </TabPanel>
                <TabPanel
                    value={tab}
                    index={2}
                >
                    <CompanyForm handleClose={handleCloseModal}/>
                </TabPanel>
                <TabPanel
                    value={tab}
                    index={3}
                >
                    <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                        <EmployeeForm handleClose={handleCloseModal}/>
                    </Box>
                </TabPanel>
            </AMPModal>
        </Box>
    );
};
